import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { DialogTitle } from '@/components/ui/dialog';
import {
  FolderOpen, FileText, Package, Layers, Users, Search, Command as CommandIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: 'bg-slate-100 text-slate-600',
  active: 'bg-blue-50 text-blue-700',
  analyzing: 'bg-cyan-50 text-cyan-700',
  completed: 'bg-emerald-50 text-emerald-700',
  quoted: 'bg-violet-50 text-violet-700',
  won: 'bg-green-50 text-green-700',
  lost: 'bg-red-50 text-red-600',
  expired: 'bg-orange-50 text-orange-600',
  draft: 'bg-slate-100 text-slate-600',
  sent: 'bg-blue-50 text-blue-700',
  accepted: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-600',
  admin: 'bg-purple-100 text-purple-700',
  manager: 'bg-blue-100 text-blue-700',
  estimator: 'bg-cyan-50 text-cyan-700',
  viewer: 'bg-gray-100 text-gray-600',
};

export default function CommandSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/search?q=${encodeURIComponent(query)}&limit=5`, {
          credentials: 'include',
        });
        if (res.ok) {
          setResults(await res.json());
        }
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const go = useCallback((path) => {
    setOpen(false);
    setQuery('');
    setResults(null);
    navigate(path);
  }, [navigate]);

  const hasResults = results && (
    results.projects?.length || results.quotes?.length ||
    results.items?.length || results.assemblies?.length || results.team?.length
  );

  const fmtPrice = (v) => v ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '';

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-trigger"
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#E2E8ED] bg-white/60 hover:bg-white hover:border-[#72A4BF]/40 transition-all text-sm text-[#72A4BF] cursor-pointer group"
      >
        <Search className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Search...</span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 ml-2 px-1.5 py-0.5 text-[10px] font-medium bg-[#0D1830]/5 text-[#446E87] rounded border border-[#E2E8ED] group-hover:border-[#72A4BF]/30">
          <CommandIcon className="w-2.5 h-2.5" />K
        </kbd>
      </button>

      {/* Command dialog */}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <DialogTitle className="sr-only">Search</DialogTitle>
        <CommandInput
          placeholder="Search projects, quotes, items, assemblies, team..."
          value={query}
          onValueChange={setQuery}
          data-testid="global-search-input"
        />
        <CommandList>
          {loading && (
            <div className="py-6 text-center text-sm text-[#72A4BF]">Searching...</div>
          )}

          {!loading && query.length >= 2 && !hasResults && (
            <CommandEmpty>No results found for "{query}"</CommandEmpty>
          )}

          {!loading && (!query || query.length < 2) && (
            <div className="py-8 text-center">
              <Search className="w-10 h-10 text-[#E2E8ED] mx-auto mb-3" />
              <p className="text-sm text-[#72A4BF]">Type at least 2 characters to search</p>
              <p className="text-xs text-[#72A4BF]/60 mt-1">Search across projects, quotes, items, assemblies & team</p>
            </div>
          )}

          {/* Projects */}
          {results?.projects?.length > 0 && (
            <CommandGroup heading="Projects">
              {results.projects.map((p) => (
                <CommandItem
                  key={p.project_id}
                  value={`project-${p.project_id}-${p.name}`}
                  onSelect={() => go(`/projects/${p.project_id}`)}
                  data-testid={`search-result-project-${p.project_id}`}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#0D1830]/5 flex items-center justify-center shrink-0">
                    <FolderOpen className="w-4 h-4 text-[#1D4053]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0D1830] truncate">{p.name}</p>
                    {p.client_name && (
                      <p className="text-xs text-[#72A4BF] truncate">{p.client_name}</p>
                    )}
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_STYLES[p.status] || ''}`}>
                    {p.status}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {results?.projects?.length > 0 && (results?.quotes?.length > 0 || results?.items?.length > 0 || results?.assemblies?.length > 0 || results?.team?.length > 0) && (
            <CommandSeparator />
          )}

          {/* Quotes */}
          {results?.quotes?.length > 0 && (
            <CommandGroup heading="Quotes">
              {results.quotes.map((q) => (
                <CommandItem
                  key={q.quote_id}
                  value={`quote-${q.quote_id}-${q.quote_name}`}
                  onSelect={() => go(`/projects/${q.project_id}`)}
                  data-testid={`search-result-quote-${q.quote_id}`}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-violet-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0D1830] truncate">{q.quote_name}</p>
                    {q.customer_name && (
                      <p className="text-xs text-[#72A4BF] truncate">{q.customer_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {q.total_price > 0 && (
                      <span className="text-xs font-semibold text-[#1D4053]">{fmtPrice(q.total_price)}</span>
                    )}
                    <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[q.status] || ''}`}>
                      {q.status}
                    </Badge>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {results?.quotes?.length > 0 && (results?.items?.length > 0 || results?.assemblies?.length > 0 || results?.team?.length > 0) && (
            <CommandSeparator />
          )}

          {/* Items */}
          {results?.items?.length > 0 && (
            <CommandGroup heading="Catalog Items">
              {results.items.map((item) => (
                <CommandItem
                  key={item.item_id}
                  value={`item-${item.item_id}-${item.description}`}
                  onSelect={() => go('/items')}
                  data-testid={`search-result-item-${item.item_id}`}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                    <Package className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0D1830] truncate">{item.description}</p>
                    <p className="text-xs text-[#72A4BF] truncate">
                      {[item.item_number, item.category].filter(Boolean).join(' / ')}
                    </p>
                  </div>
                  {item.price > 0 && (
                    <span className="text-xs font-semibold text-[#1D4053] shrink-0">{fmtPrice(item.price)}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {results?.items?.length > 0 && (results?.assemblies?.length > 0 || results?.team?.length > 0) && (
            <CommandSeparator />
          )}

          {/* Assemblies */}
          {results?.assemblies?.length > 0 && (
            <CommandGroup heading="Assemblies">
              {results.assemblies.map((a) => (
                <CommandItem
                  key={a.assembly_id}
                  value={`assembly-${a.assembly_id}-${a.assembly_name}`}
                  onSelect={() => go('/assemblies')}
                  data-testid={`search-result-assembly-${a.assembly_id}`}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                    <Layers className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0D1830] truncate">{a.assembly_name}</p>
                    <p className="text-xs text-[#72A4BF] truncate">
                      {a.assembly_type || a.description || ''}
                    </p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {results?.assemblies?.length > 0 && results?.team?.length > 0 && (
            <CommandSeparator />
          )}

          {/* Team */}
          {results?.team?.length > 0 && (
            <CommandGroup heading="Team Members">
              {results.team.map((t) => (
                <CommandItem
                  key={t.user_id}
                  value={`team-${t.user_id}-${t.name}`}
                  onSelect={() => go('/settings')}
                  data-testid={`search-result-team-${t.user_id}`}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  {t.picture ? (
                    <img src={t.picture} alt="" className="w-8 h-8 rounded-full shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#1D4053]/10 flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-[#1D4053]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0D1830] truncate">{t.name}</p>
                    <p className="text-xs text-[#72A4BF] truncate">{t.email}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_STYLES[t.role] || ''}`}>
                    {t.role}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>

        {/* Footer */}
        <div className="border-t border-[#E2E8ED] px-3 py-2 flex items-center justify-between text-xs text-[#72A4BF]">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-[#0D1830]/5 rounded border border-[#E2E8ED] text-[10px]">↑↓</kbd> navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-[#0D1830]/5 rounded border border-[#E2E8ED] text-[10px]">↵</kbd> open</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-[#0D1830]/5 rounded border border-[#E2E8ED] text-[10px]">esc</kbd> close</span>
          </div>
        </div>
      </CommandDialog>
    </>
  );
}
